import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
    BUTTON_TYPE,
    COLORS,
    DIVIDER_SIZE,
} from '@helpers/constants'
import Text from '@components/Text'
import Button from '@components/Button'
import Divider from '@components/Divider'
import ProgressBar from '@components/ProgressBar'
import ValidateQuizAnswers from '@components/ValidateQuizAnswers'
import Form from '@components/Form'
import BlockRenderer from '@views/BlockRenderer'

const QuizView = (props) => {
    const { type, questions, closePanel } = props

    const [atQuestion, setAtQuestion] = useState(0)
    const [score, setScore] = useState(0)
    const [correctedAnswers, setCorrectedAnswers] = useState([])
    const [selectedAnswers, setSelectedAnswers] = useState(
        Array(questions?.length).fill(-1)
    )

    const inShowAnswerState = type === 'quizExercise'
    const [showAnswer, setShowAnswer] = useState(false)
    const [inReview, setInReview] = useState(false)

    // if selected answers and index is -1 (empty), disable button
    const disableButton = selectedAnswers[atQuestion] === -1
    const { question, answers } = questions[atQuestion]
        ? questions[atQuestion]
        : { question: null, answers: [] }

    const registerAnswer = (index) => {
        selectedAnswers[atQuestion] = index
        setSelectedAnswers([...selectedAnswers])
    }

    // restarts the quiz, all states reset.
    const restartQuiz = () => {
        setInReview(false)
        setShowAnswer(false)
        setAtQuestion(0)
        setScore(0)
        setCorrectedAnswers([])
        setSelectedAnswers(Array(questions?.length).fill(-1))
    }

    // review questions
    const reviewQuestions = () => {
        setInReview(true)
        setShowAnswer(true)
        setAtQuestion(0)
    }

    // next questions adds a score if the answer is correct.
    const nextQuestion = () => {
        if (answers[selectedAnswers[atQuestion]].correct) {
            setScore(score + 1)
        }

        //     if (
        //         questions[atQuestion].answers[selectedAnswers[atQuestion]].correct
        //     ) {
        //         setScore(score + 1)
        //     }

        setAtQuestion(atQuestion + 1)
        if (!inReview) setShowAnswer(false)

        if (atQuestion === questions.length - 1) {
            const corrected = questions.map(({ answers }, index) => {
                return answers[selectedAnswers[index]].correct
            })
            setCorrectedAnswers(corrected)
        }
    }

    const showValidateQuizAnswers = () => {
        if (inReview) return false

        if (inShowAnswerState) {
            return !showAnswer && correctedAnswers.length >= questions.length
        } else {
            return correctedAnswers.length >= questions.length
        }
    }

    return (
        <div>
            <Divider size={DIVIDER_SIZE.SMALL} />
            <ProgressBar
                questionNum={questions.length}
                atQuestion={atQuestion}
                correctedAnswers={correctedAnswers}
            />
            {atQuestion < questions.length && (
                <>
                    <Divider size={DIVIDER_SIZE.SMALL} />
                    <Divider size={DIVIDER_SIZE.SMALL} />

                    <Text>
                        Fråga {atQuestion + 1} av {questions.length}
                    </Text>
                    <BlockRenderer content={question} />

                    <Divider size={DIVIDER_SIZE.SMALL} />

                    {answers.map((answerItem, index) => {
                        const { text } = answerItem
                        const textArea = answerItem.textArea && JSON.parse(answerItem.textArea)

                        const getMarkCorrect = () => {
                            if (!inShowAnswerState && !inReview) return false

                            if (
                                selectedAnswers[atQuestion] === index &&
                                correctedAnswers[atQuestion] === true
                            ) {
                                return true
                            } else if (
                                showAnswer &&
                                questions[atQuestion].answers[index]?.correct
                            ) {
                                return true
                            }

                            return false
                        }

                        const getMarkIncorrect = () => {
                            if (!inShowAnswerState && !inReview) return false

                            if (
                                selectedAnswers[atQuestion] === index &&
                                correctedAnswers[atQuestion] === false
                            ) {
                                return true
                            }

                            return false
                        }

                        return (
                            <div key={index} className={'mb-2'}>
                                <div className={'flex flex-row'}>
                                    <Form.QuizRadio
                                        name={atQuestion + 'answer'}
                                        id={atQuestion + 'answer' + index}
                                        value={index}
                                        checked={
                                            selectedAnswers[atQuestion] === index
                                        }
                                        onChange={(e) => {
                                            registerAnswer(index)
                                        }}
                                        disabled={showAnswer}
                                        markCorrect={getMarkCorrect()}
                                        markIncorrect={getMarkIncorrect()}
                                    />
                                    <label className={showAnswer ? '' : 'cursor-pointer'} htmlFor={atQuestion + 'answer' + index}>
                                        <BlockRenderer content={text} />
                                    </label>
                                </div>

                                {type === 'quizExercise' && (Object.keys(textArea).length > 0 && !textArea.isEmpty) && showAnswer && questions[atQuestion].answers[index]?.correct && (
                                    <div
                                        className={
                                            `bg-lightGreen p-3 rounded-md mt-2 mb-4`
                                        }
                                    >
                                        <BlockRenderer content={textArea} />
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    <Divider size={DIVIDER_SIZE.SMALL} />

                    {atQuestion === questions.length - 1 && inReview &&
                        <Button
                            disabled={disableButton}
                            type={BUTTON_TYPE.SECONDARY}
                            handleClick={restartQuiz}
                        >
                            Gör om
                        </Button>
                    }
                    {type === 'quizExercise' && !showAnswer &&
                        <Button
                            disabled={disableButton}
                            type={BUTTON_TYPE.PRIMARY}
                            handleClick={() => {
                                setShowAnswer(true)
                                setCorrectedAnswers([
                                    ...correctedAnswers,
                                    answers[selectedAnswers[atQuestion]]
                                        .correct,
                                ])
                            }}
                        >
                            Okej
                        </Button>
                    }
                    {atQuestion < questions.length - 1 && (showAnswer || type === 'quizQuestions') &&
                        <Button
                            disabled={disableButton}
                            type={BUTTON_TYPE.PRIMARY}
                            handleClick={nextQuestion}
                        >
                            Nästa fråga
                        </Button>
                    }
                    {atQuestion === questions.length - 1 && !inReview && (showAnswer || type === 'quizQuestions') &&
                        <Button
                            disabled={disableButton}
                            type={BUTTON_TYPE.PRIMARY}
                            handleClick={nextQuestion}
                        >
                            Slutför
                        </Button>
                    }
                </>
            )}

            {showValidateQuizAnswers() && (
                <ValidateQuizAnswers
                    score={score}
                    questions={questions}
                    restartQuiz={restartQuiz}
                    reviewQuestions={reviewQuestions}
                    closePanel={closePanel}
                />
            )}
        </div>
    )
}

QuizView.propTypes = {
    type: PropTypes.string,
    questions: PropTypes.array,
    closePanel: PropTypes.func,
}

export default QuizView
